import { CellPosition } from '../../types';
import { ExtractedAddress } from '../address/formulaAddress';

export type ValidationAndFormatQueue = ExtractedAddress & {
  id: string;
};

export enum QueueItemType {
  Error = 'Error',
  Cell = 'Cell',
}

type QueueItemError = {
  type: QueueItemType.Error;
  circularError: boolean;
};

type QueueItemCell = ExtractedAddress & {
  type: QueueItemType.Cell;
  position: CellPosition;
};

export type QueueItem = QueueItemCell | QueueItemError;

export interface QueueSet {
  queue: Queue;
  visited: QueueMap;
  waiting: QueueMap;
  validationsQueue: ValidationAndFormatQueue[];
  conditionalFormatsQueue: ValidationAndFormatQueue[];
  circularError?: string;
}

export type QueueMap = Map<string, Set<string>>;

export class LinkedListNode<T> {
  private _previous: LinkedListNode<T> | null;

  private _next: LinkedListNode<T> | null;

  private _item: T | null;

  constructor(item: T) {
    this._item = item;
    this._previous = null;
    this._next = null;
  }

  get item(): T | null {
    return this._item;
  }

  set item(item: T | null) {
    this._item = item;
  }

  get previous(): LinkedListNode<T> | null {
    return this._previous;
  }

  set previous(previous: LinkedListNode<T> | null) {
    this._previous = previous;
  }

  get next(): LinkedListNode<T> | null {
    return this._next;
  }

  set next(next: LinkedListNode<T> | null) {
    this._next = next;
  }
}

export class Queue {
  private _length: number;

  private _head: LinkedListNode<QueueItem> | null;

  private _tail: LinkedListNode<QueueItem> | null;

  constructor() {
    this._length = 0;
    this._head = null;
    this._tail = null;
  }

  enqueue(ref: QueueItem): void {
    const node = new LinkedListNode(ref);

    if (this._head === null) {
      this._head = node;
      this._tail = node;
    } else {
      this._tail!.next = node;
      node.previous = this._tail!;
      this._tail = node;
    }

    this._length += 1;
  }

  dequeue(): QueueItem | null {
    if (this.isEmpty()) throw new Error('Cannot dequeue an empty Queue');

    const item = this._head?.item ?? null;

    this._head = this._head?.next ?? null;

    // @ts-ignore
    if (this._head) this._head.previous = null;

    this._length -= 1;

    if (this.isEmpty()) this._tail = null;
    return item;
  }

  isEmpty(): boolean {
    return !this._length;
  }
}
